import { FileText } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  decideOnboardingProforma,
  downloadOnboardingProforma,
  getOnboardingProforma,
  type PortalProformaState,
} from "@/features/portal/api";
import { cn } from "@/lib/utils";

/** Client-visible PI status → badge colour (soft status pills). Only proposals
 * actually sent to the client appear, so just these three statuses occur. */
const PI_HISTORY_BADGE: Record<string, string> = {
  APPROVED: "bg-warning-soft text-warning", // Awaiting your response
  ACCEPTED: "bg-success-soft text-success",
  DECLINED: "bg-destructive/10 text-destructive",
};

/**
 * The engagement Proforma Invoice (PI) the firm sent for the client to approve
 * before work begins. Fetches its own state and renders only when there's a PI
 * awaiting this client's decision (stage APPROVED). Approving auto-advances the
 * onboarding to "Client accepted"; declining returns it to the firm to revise.
 * Separate from the billing "Proposals" card.
 */
export function ProformaApprovalCard() {
  const mounted = useRef(true);
  const [state, setState] = useState<PortalProformaState | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    mounted.current = true;
    getOnboardingProforma()
      .then((s) => mounted.current && setState(s))
      .catch(() => {
        /* no card if we can't load it */
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  if (!state || (!state.canDecide && state.history.length === 0)) return null;

  async function decide(decision: "accept" | "reject", note = "") {
    setBusy(true);
    try {
      const next = await decideOnboardingProforma(decision, note);
      if (!mounted.current) return;
      setState(next);
      toast.success(
        decision === "accept"
          ? "Thank you — your approval is recorded."
          : "Your response was sent to your tax team.",
      );
    } catch (err) {
      if (mounted.current) {
        toast.error(err instanceof Error ? err.message : "Something went wrong.");
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function view() {
    try {
      const blob = await downloadOnboardingProforma();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Couldn't open the document.");
    }
  }

  return (
    <Card className="border-2 p-6">
      <div className="flex flex-col gap-4">
        {state.canDecide ? (
          <>
            <div className="flex items-start gap-4">
              <div className="bg-primary-soft text-warning grid size-12 shrink-0 place-items-center rounded-sm">
                <FileText className="size-6" />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading text-xl font-bold text-foreground">
                  Review your proposal
                </h2>
                <p className="text-muted-foreground mt-1 text-sm">
                  Your tax team shared a proposal for your engagement. Please review
                  it, then approve to get started or decline with a note.
                </p>
              </div>
            </div>

            <div>
              <Button type="button" variant="link" className="px-0" onClick={() => void view()}>
                View proposal (PDF)
              </Button>
            </div>

            {rejecting ? (
              <div className="flex flex-col gap-3">
                <Textarea
                  aria-label="Reason (optional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Let your team know what you'd like changed (optional)…"
                  disabled={busy}
                />
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setRejecting(false);
                      setReason("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={busy}
                    onClick={() => void decide("reject", reason.trim())}
                  >
                    Send response
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="cta"
                  disabled={busy}
                  onClick={() => void decide("accept")}
                >
                  Approve proposal
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setRejecting(true)}
                >
                  Decline
                </Button>
              </div>
            )}
          </>
        ) : (
          <h2 className="font-heading text-xl font-bold text-foreground">Your proposals</h2>
        )}

        {state.history.length > 0 && (
          <ul className={cn("flex flex-col gap-2", state.canDecide && "border-t pt-4")}>
            {state.history.map((item, i) => (
              <li
                key={i}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span className="flex items-center gap-2">
                  <FileText className="text-muted-foreground size-4" />
                  Proposal {item.proposalNumber}
                </span>
                <span className="flex items-center gap-2">
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-xs font-semibold",
                      PI_HISTORY_BADGE[item.status] ?? "bg-muted text-muted-foreground",
                    )}
                  >
                    {item.label}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {new Date(item.decidedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
